import { AuthProvider } from "react-admin";
import sendRequest from "./dataProvider/sendRequest";
import { access } from "fs";

export const AUTH_REDIRECT_MESSAGE_KEY = "auth_redirect_message";
const TOKEN_LAST_REFRESH_AT_KEY = "token_last_refresh_at";
const TOKEN_EXPIRES_IN_KEY = "token_expires_in";
// const TOKEN_REFRESH_BUFFER_SECONDS = 60;
// const TOKEN_REFRESH_BUFFER_SECONDS = 300;
const TOKEN_REFRESH_BUFFER_SECONDS = 1800;
let refreshTokenPromise: Promise<string | null> | null = null;
const SESSION_EXPIRED_MESSAGE =
  "Your session has expired. Please log in again.";
const AUTH_EXEMPT_ROUTE_PATTERNS = [
  /^\/login(?:\/|$)/,
  /^\/paymentSchedule(?:\/|$)/,
  /^\/pdfs(?:\/|$)/,
  /^\/employer-verify(?:\/|$)/,
  /^\/employerVerify(?:\/|$)/,
  /^\/vendor-verify(?:\/|$)/,
  /^\/vendors\/verify(?:\/|$)/,
  /^\/reset-password(?:\/|$)/,
  /^\/forget-password(?:\/|$)/,
  /^\/verify-banker(?:\/|$)/,
  /^\/register\/banker(?:\/|$)/,
  /^\/verifyBanker(?:\/|$)/,
  /^\/loanmodifypdf(?:\/|$)/,
];

const STANDALONE_ROUTE_PATTERNS = AUTH_EXEMPT_ROUTE_PATTERNS.filter(
  (pattern) => pattern.source !== /^\/login(?:\/|$)/.source
);

export function isPublicRoute(pathname = window.location.pathname) {
  return AUTH_EXEMPT_ROUTE_PATTERNS.some((pattern) => pattern.test(pathname));
}

export function isStandaloneRoute(pathname = window.location.pathname) {
  return STANDALONE_ROUTE_PATTERNS.some((pattern) => pattern.test(pathname));
}

export function setAuthRedirectMessage(message: string) {
  sessionStorage.setItem(AUTH_REDIRECT_MESSAGE_KEY, message);
}

export function consumeAuthRedirectMessage() {
  const message = sessionStorage.getItem(AUTH_REDIRECT_MESSAGE_KEY);

  if (message) {
    sessionStorage.removeItem(AUTH_REDIRECT_MESSAGE_KEY);
  }

  return message;
}

export function redirectToLogin(message?: string) {
  if (isPublicRoute()) {
    return;
  }

  if (message) {
    setAuthRedirectMessage(message);
  }

  window.location.href = "/login";
}

export function handleExpiredSession(message = SESSION_EXPIRED_MESSAGE) {
  clearStoredAuthState();
  redirectToLogin(message);
}

export function saveTokenRefreshMeta(expiresIn: unknown) {
  const expiresSeconds =
    typeof expiresIn === "number" ? expiresIn : Number(expiresIn);

  if (!Number.isFinite(expiresSeconds) || expiresSeconds <= 0) {
    return;
  }

  localStorage.setItem(
    TOKEN_EXPIRES_IN_KEY,
    String(Math.floor(expiresSeconds))
  );
  localStorage.setItem(TOKEN_LAST_REFRESH_AT_KEY, String(Date.now()));
}

export function getSecondsUntilTokenExpiry() {
  const expiresIn = Number(localStorage.getItem(TOKEN_EXPIRES_IN_KEY) || "0");
  const lastRefreshAt = Number(
    localStorage.getItem(TOKEN_LAST_REFRESH_AT_KEY) || "0"
  );

  if (!Number.isFinite(expiresIn) || expiresIn <= 0 || !lastRefreshAt) {
    return 0;
  }

  const elapsedSeconds = Math.floor((Date.now() - lastRefreshAt) / 1000);
  return Math.max(0, expiresIn - elapsedSeconds);
}

export function shouldRefreshToken(
  bufferSeconds = TOKEN_REFRESH_BUFFER_SECONDS
) {
  return getSecondsUntilTokenExpiry() <= bufferSeconds;
}

export function clearStoredAuthState() {
  const itemsToRemove = [
    "email",
    "username",
    "access_token",
    "refresh_token",
    "bank",
    "bbaccess",
    "auth",
    "openTabs",
    "openGlobalTabs",
    "authrole",
    "product_counselors",
    "employer_proudcts",
    "all_experts",
    "division",
    "permissions",
    "mainpathname",
    "applicationMenuStats",
    "customerMenuStats",
    "loanMenuStats",
    "csrfToken",
    "XSRF-TOKEN",
    TOKEN_LAST_REFRESH_AT_KEY,
    TOKEN_EXPIRES_IN_KEY,
  ];

  itemsToRemove.forEach((item) => {
    localStorage.removeItem(item);
  });

  Object.keys(localStorage).forEach((key) => {
    if (key.startsWith("backUrl")) {
      localStorage.removeItem(key);
    }
  });
}

export async function ensureFreshToken(
  bufferSeconds = TOKEN_REFRESH_BUFFER_SECONDS
) {
  if (isPublicRoute()) {
    return localStorage.getItem("access_token");
  }

  if (!shouldRefreshToken(bufferSeconds)) {
    return localStorage.getItem("access_token");
  }

  return refreshToken();
}

export const refreshToken = async () => {
  if (isPublicRoute()) {
    return localStorage.getItem("access_token");
  }

  if (refreshTokenPromise) {
    return refreshTokenPromise;
  }

  const access_token = localStorage.getItem("access_token");
  const csrfToken = localStorage.getItem("csrfToken");

  refreshTokenPromise = fetch(import.meta.env.VITE_API_URL + "/refresh-token", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${access_token}`,
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-CSRF-TOKEN": `${csrfToken}`,
    },
    body: JSON.stringify({
      refresh_token: localStorage.getItem("refresh_token"),
    }),
  })
    .then(async (response) => {
      if (!response.ok) {
        // console.log(
        //   "Failed to refresh token, response status:",
        //   response.status
        // );
        throw new Error("Failed to refresh token");
      }

      const data = await response.json();
      const newToken = data.token || data.access_token;

      if (!newToken) {
        throw new Error("Missing refreshed token");
      }

      localStorage.setItem("access_token", newToken);
      saveTokenRefreshMeta(data.expires_in);
      console.log(
        "Token refreshed and saved:",
        newToken.substring(0, 20) + "..."
      );

      return newToken;
    })
    .catch((error) => {
      handleExpiredSession();
      throw error;
    })
    .finally(() => {
      refreshTokenPromise = null;
    });

  return refreshTokenPromise;
};

const authProvider: AuthProvider = {
  login: async ({ email, password, recaptchaToken }) => {
    localStorage.removeItem("username");
    localStorage.removeItem("access_token");
    localStorage.removeItem("bank");
    localStorage.removeItem("auth");
    localStorage.removeItem("openTabs");
    localStorage.removeItem("openGlobalTabs");

    //1. first get CSRF cookie (browser environment only)
    await fetch(`${import.meta.env.VITE_API_WEB_URL}/sanctum/csrf-cookie`, {
      credentials: "include", // must include credentials
    });
    const request = new Request(import.meta.env.VITE_API_URL + "/login", {
      method: "POST",
      body: JSON.stringify({
        email,
        password,
        recaptchaToken,
        recaptchaAction: "LOGIN",
      }),
      headers: new Headers({ "Content-Type": "application/json" }),
    });
    return fetch(request)
      .then((response) => {
        // console.log("response.status", response.status, response);

        if (
          response.headers.has("Set-Cookie") &&
          response.headers.get("Set-Cookie")?.includes("XSRF-TOKEN")
        ) {
          const cookies = response.headers?.get("Set-Cookie")?.split(";");
          const xsrfToken = cookies
            ?.find((cookie) => cookie?.includes("XSRF-TOKEN"))
            ?.split("=")[1]
            ?.trim();
          localStorage.setItem("XSRF-TOKEN", xsrfToken || ""); // Save new XSRF-TOKEN
        }

        if (response.status < 200 || response.status >= 300) {
          return Promise.reject();
          //throw new Error(response.message);
        } else {
          return response.json();
        }
      })
      .then((auth) => {
        // console.log("auth===>", auth);

        if (auth?.message == "ip error") {
          localStorage.setItem("access_token", auth.access_token);
          saveTokenRefreshMeta(auth.expires_in);
          // window.location.href = "/verify-banker";
          window.location.replace("/verify-banker");
          return new Promise(() => {});
        } else {
          if (auth.status == 200) {
            auth.id = auth.user.id;
            auth.name = auth.user.name;
            auth.email = auth.user.email;
            auth.bank = auth.user.bank;
            auth.avatar = auth.user.avatar;
            auth.bankadmin = auth.user.bankadmin;
            localStorage.setItem("bbaccess", auth.user.bbaccess);
            localStorage.setItem("username", auth.name);
            localStorage.setItem("email", auth.email);
            localStorage.setItem("access_token", auth.access_token);
            saveTokenRefreshMeta(auth.expires_in);
            // localStorage.setItem('csrfToken', auth.csrfToken);

            // localStorage.setItem("auth", JSON.stringify(auth));
            localStorage.setItem("division", "Personal");
            localStorage.setItem(
              "authrole",
              auth.user.admin ? "admin" : auth.user.bankadmin ? "bankadmin" : ""
            );
            if (auth.user.admin) {
              localStorage.setItem("bank", "");
              localStorage.setItem("bankId", "");
            } else {
              localStorage.setItem("bank", auth.bank);
              localStorage.setItem("bankId", auth.user.bankId);
            }

            localStorage.setItem("all_experts", "");

            // PreloadMenuStats();
            return Promise.resolve();
          } else {
            return Promise.reject(auth.message);
          }
        }
      })
      .catch(() => {
        return Promise.reject("Invalid email or password");
      });
  },
  logout: async () => {
    const access_token = localStorage.getItem("access_token");
    const csrfToken = localStorage.getItem("csrfToken");
    try {
      const response = await fetch(import.meta.env.VITE_API_URL + "/logout", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${access_token}`,
          "X-CSRF-TOKEN": `${csrfToken}`,
          "X-XSRF-TOKEN": `${csrfToken}`,
        },
      });

      const data = await response.json();
      console.log("Logout response:", data);
    } catch (error) {
      console.error("Logout API error:", error);
    } finally {
      clearStoredAuthState();

      //redirect explicitly to login page
      redirectToLogin();
    }
  },
  checkError: ({ status }: { status: number }) => {
    console.log("run checkError " + status);
    if (status === 401 || status === 403) {
      clearStoredAuthState();
      return Promise.reject(SESSION_EXPIRED_MESSAGE);
    } else if (status == 429) {
      return Promise.reject("Invalid identity [error code : " + status + "]");
    } else if (status === 500) {
      //localStorage.removeItem("username");
      // return Promise.reject('Server Error');
    }
    return Promise.resolve();
  },
  checkAuth: () => {
    if (isPublicRoute()) {
      return Promise.resolve();
    }

    const username = localStorage.getItem("username");
    const access_token = localStorage.getItem("access_token");

    if (!username || !access_token) {
      redirectToLogin();
      return Promise.reject();
    }

    if (shouldRefreshToken()) {
      return ensureFreshToken()
        .then(() => Promise.resolve())
        .catch(() => {
          handleExpiredSession();
          return Promise.reject();
        });
    }

    return Promise.resolve();
  },
  getPermissions: () => {
    const role = localStorage.getItem("bbaccess");
    return role ? Promise.resolve(role) : Promise.reject();
  },
  getIdentity: () => {
    try {
      const { id, name, email, avatar, bankadmin } = JSON.parse(
        localStorage.getItem("auth") || "{}"
      );
      // // const fullName = name;
      // const fullName = email;
      const fullName = localStorage.getItem("email") || "";
      return Promise.resolve({ id, fullName, avatar, bankadmin });
    } catch (error) {
      // window.location.href = "/login";
      return Promise.reject(error);
    }
  },
};

export default authProvider;
function PreloadMenuStats() {
  throw new Error("Function not implemented.");
}
